(function () {
const PINCODE_RE = /^[1-9][0-9]{5}$/;
const PINCODE_EVENT = 'earthen:pincode-update';
// Shown until a pincode is known. Amazon is deliberately NOT called in that
// state: with no postalCode it answers with a nationwide worst case (a date a
// week or more out), which reads as a poor promise rather than a helpful one.
// A flat range is both honest and better-looking than a pessimistic exact date.
const GENERIC_DELIVERY_TEXT = 'Delivery in 2-4 days';
const shiprocketPromiseCache = new Map();

// A signed-in customer's default address, rendered into the control by Liquid.
// Lowest priority on purpose: an explicitly entered pincode must survive.
function defaultPincodeFromDom() {
  const element = document.querySelector('[data-default-pincode]');
  const zip = (element?.dataset.defaultPincode || '').trim();
  return PINCODE_RE.test(zip) ? zip : '';
}

function knownPincode() {
  try {
    const savedPincode = window.localStorage.getItem('earthen_pincode') || '';
    if (PINCODE_RE.test(savedPincode)) return savedPincode;

    const sessionPincode = window.sessionStorage.getItem('pincode') || '';
    if (PINCODE_RE.test(sessionPincode)) return sessionPincode;
  } catch (error) {
    /* Private browsing can block storage; fall through to the account default. */
  }
  return defaultPincodeFromDom();
}

function rememberPincode(pincode) {
  try {
    window.localStorage.setItem('earthen_pincode', pincode);
    window.sessionStorage.setItem('pincode', pincode);
  } catch (error) {
    /* Private browsing can block storage; the current check still works. */
  }
}

function ordinalSuffix(day) {
  const mod100 = day % 100;
  if (mod100 >= 11 && mod100 <= 13) return 'th';
  if (day % 10 === 1) return 'st';
  if (day % 10 === 2) return 'nd';
  if (day % 10 === 3) return 'rd';
  return 'th';
}

function formatDeliveryDate(value) {
  if (!value) return '';
  const source = String(value).trim();
  // Two upstream shapes, and both already carry the correct weekday:
  //   delivery app  "Wed, 8 Jul"      weekday, day, month
  //   Amazon        "Monday, Sep 07"  weekday, month, day
  // Always use the weekday they send. Never re-derive it from new Date(source):
  // neither shape carries a year, so V8 defaults to 2001 and the weekday comes
  // back wrong (Amazon's "Monday, Sep 07" rendered as "Friday, 7th September").
  const dayFirst = source.match(
    /^(Mon|Tue|Wed|Thu|Fri|Sat|Sun)[a-z]*,?\s+(\d{1,2})(?:st|nd|rd|th)?\s+([A-Za-z]{3,9})/i
  );
  const monthFirst = dayFirst
    ? null
    : source.match(
        /^(Mon|Tue|Wed|Thu|Fri|Sat|Sun)[a-z]*,?\s+([A-Za-z]{3,9})\.?\s+(\d{1,2})(?:st|nd|rd|th)?/i
      );
  const weekdays = {
    mon: 'Monday',
    tue: 'Tuesday',
    wed: 'Wednesday',
    thu: 'Thursday',
    fri: 'Friday',
    sat: 'Saturday',
    sun: 'Sunday',
  };
  const months = {
    jan: 'January',
    feb: 'February',
    mar: 'March',
    apr: 'April',
    may: 'May',
    jun: 'June',
    jul: 'July',
    aug: 'August',
    sep: 'September',
    oct: 'October',
    nov: 'November',
    dec: 'December',
  };
  let weekdayToken;
  let monthToken;
  let day;
  if (dayFirst) {
    weekdayToken = dayFirst[1];
    day = Number(dayFirst[2]);
    monthToken = dayFirst[3];
  } else if (monthFirst) {
    weekdayToken = monthFirst[1];
    day = Number(monthFirst[3]);
    monthToken = monthFirst[2];
  } else {
    // Relative wording ("Tomorrow", "Today") and anything else without an
    // explicit year is passed through untouched, since Date would invent a year.
    if (!/\d{4}/.test(source)) return String(value);
    const parsed = new Date(source);
    if (Number.isNaN(parsed.getTime())) return String(value);
    const parsedDay = parsed.getDate();
    return (
      new Intl.DateTimeFormat('en-IN', { weekday: 'long' }).format(parsed) +
      ', ' +
      parsedDay +
      ordinalSuffix(parsedDay) +
      ' ' +
      new Intl.DateTimeFormat('en-IN', { month: 'long' }).format(parsed)
    );
  }

  const weekday = weekdays[weekdayToken.slice(0, 3).toLowerCase()];
  const month = months[monthToken.slice(0, 3).toLowerCase()];
  if (!weekday || !month || !day) return String(value);

  return `${weekday}, ${day}${ordinalSuffix(day)} ${month}`;
}

// The control is always present; this only swaps which half of it is showing.
// `editing` is set by the Change button (and by an unresolvable pincode) so the
// entry form can be reopened without discarding the stored value.

// A promise every item shares is a cart-level fact, not per-row information, so
// it belongs on the pincode band next to the control that changes it - not
// stranded under the first product. Per-item lines are only used when items
// genuinely differ.
function setCartPromise(promiseText, labelText) {
  document.querySelectorAll('earthen-cart-pincode').forEach((element) => {
    const promise = element.querySelector('[data-cart-delivery-promise]');
    const label = element.querySelector('[data-cart-pincode-label]');
    if (promise) promise.textContent = promiseText || 'Delivering';
    if (label) label.textContent = labelText || 'Check delivery date';
  });
}

function syncPincodeUi(pincode) {
  const hasPincode = PINCODE_RE.test(pincode || '');
  document.querySelectorAll('earthen-cart-pincode').forEach((element) => {
    const editing = element.dataset.editing === 'true';
    const summary = element.querySelector('[data-cart-pincode-summary]');
    const entry = element.querySelector('[data-cart-pincode-entry]');
    const current = element.querySelector('[data-cart-pincode-current]');
    const input = element.querySelector('[data-cart-pincode-input]');

    if (hasPincode && current) current.textContent = pincode;
    if (hasPincode && input && document.activeElement !== input) input.value = pincode;
    if (summary) summary.hidden = !hasPincode || editing;
    if (entry) entry.hidden = hasPincode && !editing;
  });
}

class EarthenCartPincodeController {
  constructor(element) {
    this.element = element;
    if (this.element.dataset.ready) return;
    this.element.dataset.ready = 'true';
    this.form = this.element.querySelector('[data-cart-pincode-form]');
    this.input = this.element.querySelector('[data-cart-pincode-input]');
    this.error = this.element.querySelector('[data-cart-pincode-error]');

    const saved = knownPincode();
    if (saved && this.input) this.input.value = saved;
    syncPincodeUi(saved);

    this.input?.addEventListener('input', () => {
      this.input.value = this.input.value.replace(/\D/g, '').slice(0, 6);
      if (this.error) this.error.hidden = true;
    });

    // Change reopens the form without clearing the stored pincode, so a
    // customer can re-check as many pincodes as they like.
    this.element
      .querySelector('[data-cart-pincode-change]')
      ?.addEventListener('click', () => {
        this.element.dataset.editing = 'true';
        syncPincodeUi(knownPincode());
        this.input?.focus();
        this.input?.select();
      });

    this.form?.addEventListener('submit', (event) => {
      event.preventDefault();
      const pincode = (this.input?.value || '').trim();
      if (!PINCODE_RE.test(pincode)) {
        if (this.error) {
          this.error.textContent = 'Please enter a valid 6-digit pincode.';
          this.error.hidden = false;
        }
        return;
      }

      rememberPincode(pincode);
      if (this.error) this.error.hidden = true;
      delete this.element.dataset.editing;
      syncPincodeUi(pincode);
      document.dispatchEvent(new CustomEvent(PINCODE_EVENT, { detail: { pincode } }));
    });
  }
}

class EarthenCartDeliveryListController {
  constructor(element) {
    this.element = element;
    if (this.element.dataset.ready) return;
    this.element.dataset.ready = 'true';

    // The cart drawer is rendered on every page but starts closed, so resolving
    // here would fire Amazon/Shiprocket courier calls for a drawer nobody has
    // opened. Wait for the dialog to open instead. On /cart there is no dialog
    // ancestor, so this falls through and resolves immediately as before.
    const dialog = this.element.closest('dialog') || this.element.closest('cart-drawer');
    if (dialog && !dialog.hasAttribute('open')) {
      const obs = new MutationObserver(() => {
        if (dialog.hasAttribute('open')) {
          this.resolve();
          obs.disconnect();
        }
      });
      obs.observe(dialog, { attributes: true, attributeFilter: ['open'] });
      this.dialog = dialog;
    } else {
      this.dialog = dialog;
    }

    this.handlePincodeChange = (event) => {
      const pincode = String(event.detail?.pincode || '');
      if (!PINCODE_RE.test(pincode) || (this.dialog && !this.dialog.hasAttribute('open'))) return;
      this.resolve({ force: true });
    };
    document.addEventListener(PINCODE_EVENT, this.handlePincodeChange);
    if (!this.dialog) this.resolve();
    else if (this.dialog.hasAttribute('open')) this.resolve();
  }

  records() {
    return [...this.element.querySelectorAll('[data-cart-item-delivery]')]
      .map((output) => ({
        output,
        variantId: String(output.dataset.variantId || ''),
        weightGrams: Number(output.dataset.weightGrams || 0),
        quantity: Math.max(1, Number(output.dataset.quantity || 1)),
      }))
      .filter((record) => record.variantId);
  }

  render(record, promise) {
    const output = record.output;
    output.replaceChildren();
    if (!promise?.date) {
      output.hidden = true;
      return;
    }

    // Pre-pincode range: a plain promise, with no courier attribution.
    if (promise.source === 'range' || promise.source === 'fallback') {
      output.append(document.createTextNode(promise.date));
      output.hidden = false;
      return;
    }

    output.append(document.createTextNode('Delivered by '));
    if (promise.source === 'amazon') {
      const amazon = document.createElement('span');
      amazon.className = 'earthen-cart-item-delivery__amazon';
      amazon.textContent = 'amazon';
      amazon.setAttribute('aria-label', 'Amazon');
      output.append(amazon, document.createTextNode(` by ${promise.date}`));
    } else {
      output.append(document.createTextNode(promise.date));
    }
    output.hidden = false;
  }

  async shiprocketPromise(record, pincode) {
    if (!pincode || record.weightGrams <= 0) return null;
    const weightKg = (record.weightGrams * record.quantity) / 1000;
    const cacheKey = `${pincode}:${weightKg}`;
    if (shiprocketPromiseCache.has(cacheKey)) return shiprocketPromiseCache.get(cacheKey);

    try {
      const params = new URLSearchParams({
        pincode,
        weight: String(weightKg),
      });
      const response = await fetch(`/apps/delivery/estimate?${params}`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
        signal: this.abortController?.signal,
      });
      const data = await response.json().catch(() => ({}));
      const promise =
        response.ok && data.enabled !== false && data.serviceable && data.deliveryText
          ? {
              source: data.estimateFallback ? 'fallback' : 'shiprocket',
              date: data.estimateFallback
                ? data.deliveryText
                : formatDeliveryDate(data.deliveryText),
            }
          : null;
      shiprocketPromiseCache.set(cacheKey, promise);
      return promise;
    } catch (error) {
      if (error?.name === 'AbortError') return null;
      return null;
    }
  }

  async resolve(options = {}) {
    const records = this.records();
    if (!records.length) {
      syncPincodeUi(knownPincode());
      return;
    }

    this.abortController?.abort();
    this.abortController = new AbortController();
    const pincode = knownPincode();

    // Stage 1: no pincode yet. Show the flat range once (not once per item) and
    // reveal the pincode field, which is the only way to turn this into an exact
    // date. No courier request is made at all in this state.
    if (!pincode) {
      records.forEach((record) => {
        record.output.dataset.requestKey = `range:${record.variantId}:${record.quantity}`;
        record.output.removeAttribute('aria-busy');
        record.output.replaceChildren();
        record.output.hidden = true;
      });
      setCartPromise(GENERIC_DELIVERY_TEXT, GENERIC_DELIVERY_TEXT);
      syncPincodeUi('');
      return;
    }

    records.forEach((record) => {
      record.cacheKey = `${pincode}:${record.variantId}`;
      record.output.dataset.requestKey = `${record.cacheKey}:${record.quantity}`;
      record.output.setAttribute('aria-busy', 'true');
    });

    let unresolved = 0;
    const resolved = await Promise.all(records.map(async (record) => {
      const promise = await this.shiprocketPromise(record, pincode);
      return { record, promise };
    }));

    // Every line in a multi-item cart usually carries an identical promise,
    // and repeating it under each product is noise.
    // Collapse to the first line ONLY when every item resolved to the same
    // source and date; the moment two items differ, each keeps its own line,
    // because a per-item date is real information and must not be hidden.
    // Keyed on the DATE only, not the courier. Two items can resolve through
    // different weights to the same day; that difference is invisible to the
    // customer and collapsing on it left two identical lines under a band that
    // already stated the destination.
    const signatures = resolved.map(({ promise }) => (promise ? promise.date : null));
    // Includes the single-item case: the band is the canonical place for a
    // shared date now, so a one-item cart should put it there too rather than
    // falling back to a per-row line under the only product.
    const collapse =
      signatures.length > 0 && signatures.every((value) => value && value === signatures[0]);

    resolved.forEach(({ record, promise }, index) => {
      const requestKey = `${record.cacheKey}:${record.quantity}`;
      if (record.output.dataset.requestKey !== requestKey) return;

      if (collapse) {
        record.output.replaceChildren();
        record.output.hidden = true;
      } else {
        this.render(record, promise);
      }

      record.output.removeAttribute('aria-busy');
      if (!promise) unresolved += 1;
    });

    setCartPromise(collapse ? resolved[0].promise.date : 'Delivering', 'Check delivery date');

    // A pincode that resolves nothing reopens the entry form so a different one
    // can be tried, instead of leaving a summary row that cannot be acted on.
    document.querySelectorAll('earthen-cart-pincode').forEach((element) => {
      if (unresolved > 0) {
        element.dataset.editing = 'true';
      } else {
        delete element.dataset.editing;
      }
    });
    syncPincodeUi(pincode);
  }
}

function initialiseCartEnhancements() {
  document.querySelectorAll('earthen-cart-pincode').forEach((element) => {
    try {
      if (element.dataset.ready === 'true' && element.earthenCartController) return;
      element.earthenCartController = new EarthenCartPincodeController(element);
    } catch (error) {
      console.warn('[earthen-cart] Could not initialise the pincode field.', error);
    }
  });
  document.querySelectorAll('[data-earthen-cart-delivery-list]').forEach((element) => {
    try {
      if (element.dataset.ready === 'true' && element.earthenCartController) return;
      element.earthenCartController = new EarthenCartDeliveryListController(element);
    } catch (error) {
      console.warn('[earthen-cart] Could not initialise delivery dates.', error);
    }
  });
}

class EarthenCartPincodeElement extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready !== 'true' || !this.earthenCartController) {
      this.earthenCartController = new EarthenCartPincodeController(this);
    }
  }
}

if (!customElements.get('earthen-cart-pincode')) {
  customElements.define('earthen-cart-pincode', EarthenCartPincodeElement);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initialiseCartEnhancements, { once: true });
} else {
  initialiseCartEnhancements();
}

// Scoped, debounced cart observer.
//
// The previous version observed document.documentElement with subtree:true and
// ran initialiseCartEnhancements on every DOM mutation anywhere on the page -
// two querySelectorAll calls each time, firing continuously while the loyalty
// widget, WhatsApp button and other components mutate.
//
// Selector note: this theme renders <cart-drawer-component> and
// <cart-items-component>. There is no <cart-drawer> element, and an
// id-contains match on "cart-drawer" resolves to
// <h2 id="cart-drawer-heading-empty">, so neither of those may be used as a root.
const CART_ROOT_SELECTOR = 'cart-drawer-component, cart-items-component';
const observedCartRoots = new WeakSet();
let cartInitTimer = null;

function scheduleCartInit() {
  if (cartInitTimer) return;
  cartInitTimer = setTimeout(() => {
    cartInitTimer = null;
    initialiseCartEnhancements();
  }, 100);
}

function observeCartRoots() {
  const roots = document.querySelectorAll(CART_ROOT_SELECTOR);
  roots.forEach((root) => {
    if (observedCartRoots.has(root)) return;
    observedCartRoots.add(root);
    new MutationObserver(scheduleCartInit).observe(root, { childList: true, subtree: true });
  });
  return roots.length;
}

if (observeCartRoots() === 0) {
  // No cart container in this document. Fall back to the old document-wide
  // watch so the enhancements can never silently fail to initialise.
  new MutationObserver(scheduleCartInit).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
}

document.addEventListener(PINCODE_EVENT, () => {
  document.querySelectorAll('[data-earthen-cart-delivery-list]').forEach((element) => {
    element.earthenCartController?.resolve({ force: true });
  });
});
document.addEventListener('cart:update', () => {
  // A section render replaces the cart container, so re-attach observers to the
  // new nodes before re-initialising.
  observeCartRoots();
  initialiseCartEnhancements();
  document.querySelectorAll('[data-earthen-cart-delivery-list]').forEach((element) => {
    element.earthenCartController?.resolve();
  });

  // Product and account re-order buttons can replace the drawer section while
  // this event is being handled. Resolve the live drawer after that replacement
  // so every add-to-cart action opens the drawer instead of navigating to /cart.
  window.setTimeout(() => {
    const drawer = document.querySelector('cart-drawer-component[auto-open]');
    const dialog = drawer?.querySelector('dialog');
    if (drawer && !dialog?.open) drawer.open();
  }, 50);
});
document.addEventListener('discount:update', () => {
  initialiseCartEnhancements();
  document.querySelectorAll('[data-earthen-cart-delivery-list]').forEach((element) => {
    element.earthenCartController?.resolve();
  });
});
})();
